<template>
  <section
    class="w-full max-w-7xl mx-auto [@media(max-width:1280px)]:px-[60px] py-[40px] grid gap-4 grid-cols-1 lg:grid-cols-[30%_1fr] relative"
    id="features"
  >
    <div class="comparison-viewer" ref="comparisonViewer">
      <div class="image-layer bg-layer">
        <img :src="currentBgImage" alt="Background" />
      </div>

      <div
        class="image-layer fg-layer"
        :style="{
          clipPath: `inset(${clipPosition}% 0 0 0)`,
          transition: isDragging ? 'none' : isAnimating ? 'clip-path 0.3s ease' : 'none',
        }"
      >
        <img :src="currentFgImage" alt="Foreground" />
      </div>

      <div class="w-[100%] h-[91%] absolute top-[4%]">
        <div
          class="drag-handle"
          :style="{
            top: `${clipPosition}%`,
            transition: isDragging ? 'none' : isAnimating ? 'top 0.3s ease' : 'none',
          }"
          @mousedown="startDrag"
          @touchstart="startDrag"
        >
          <div class="handle-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M7 10L12 5L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              <path d="M7 14L12 19L17 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </div>
          <div class="handle-line"></div>
        </div>
      </div>
    </div>
    <div>
      <div class="text-[36px] font-semibold leading-[1.5] mt-[30px] mr-[30px] ml-0">What Makes Women's Bible Special?</div>
      <div class="flex items-center justify-between mt-[30px] [@media(max-width:580px)]:flex-col gap-[16px]">
        <img src="@/assets/imgs/logo.png" class="w-[40px]" alt="Women's Bible logo" />
        <img src="@/assets/imgs/pageDiff/vs.png" class="w-[16px] ml-[5px]" alt="vs" />

        <div class="flex items-center gap-1">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            :class="{ active: activeTab === tab.key }"
            @click="setActiveTab(tab.key)"
            class="text-[16px] font-semibold p-[16px] rounded-[48px] bg-[#F2EBEE] whitespace-nowrap italic"
            :style="{
              backgroundColor: activeTab === tab.key ? '#444444' : '#F2EBEE',
              color: activeTab === tab.key ? '#FFFFFF' : '#47201A',
            }"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>
      <div v-for="tab in tabs" :key="tab.key" class="mt-[16px]">
        <div v-if="activeTab === tab.key" class="">
          <div class="px-[16px] my-[5px] pt-[15px] pb-[30px] bg-[#FFFFFF] border-[1px] border-[#F8CBDB] rounded-[0_30px_30px_30px]">
            <p class="text-[28px] text-[#47201A] font-semibold leading-[1.5]">
              {{ tab.key }}
            </p>
            <p v-for="(item, index) in tab.content" :key="index" class="text-[18px] text-[#444444] mt-[16px] flex items-start">
              <!-- Colored dot SVG -->
              <svg
                class="w-[8px] h-[8px] flex-shrink-0 mr-[8px] mt-[6px]"
                viewBox="0 0 12 12"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <!-- reduce radius so the circle has a small padding inside the viewBox -->
                <circle cx="4" cy="4" r="3" fill="#FF81AE" />
              </svg>
              {{ item }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

import playerBg from '@/assets/imgs/pageDiff/player_bg.png'
import playerFg from '@/assets/imgs/pageDiff/player_fg.png'
import bibleBg from '@/assets/imgs/pageDiff/bible_bg.png'
import bibleFg from '@/assets/imgs/pageDiff/bible_fg.png'
import bibleTriviaBg from '@/assets/imgs/pageDiff/bible_trivia_bg.png'
import bibleTriviaFg from '@/assets/imgs/pageDiff/bible_trivia_fg.png'

const tabs = [
  {
    key: 'Community That Truly Connects',
    label: 'Bible Learn',
    content: [
      'Most Bible apps focus on solo reading or static devotionals.',
      'We go further — fostering real community, where women share prayer requests and receive personal encouragement from others.',
    ],
    bgImage: playerBg,
    fgImage: playerFg,
  },
  {
    key: 'Prayers Tailored for Women',
    label: 'Spiritual Life',
    content: [
      'Other apps offer generic prayers or devotionals with little emotional connection.',
      'In our app, each prayer and devotional is lovingly written to reflect a woman’s daily experiences — work, family, healing, and hope.',
    ],
    bgImage: bibleBg,
    fgImage: bibleFg,
  },
  {
    key: 'Faith Learning That’s Engaging',
    label: 'Bible Quiz',
    content: [
      'Not just learning tools which often feel academic or overwhelming.',
      'Our Bible Quiz makes Scripture learning simple, joyful, and spiritually uplifting — perfect for believers at any stage.',
    ],
    bgImage: bibleTriviaBg,
    fgImage: bibleTriviaFg,
  },
]

// 默认选中第一个按钮
const activeTab = ref(tabs[0].key)

// 裁剪位置（百分比）
const clipPosition = ref(50)

// 是否正在拖动
const isDragging = ref(false)

// 动画定时器
let animationTimer: number | null = null

// 是否正在播放动画
const isAnimating = ref(false)

// 获取当前选中项
const currentItem = computed(() => {
  return tabs.find((item) => item.key === activeTab.value)
})

// 当前背景图
const currentBgImage = computed(() => {
  return currentItem.value?.bgImage || ''
})

// 当前前景图
const currentFgImage = computed(() => {
  return currentItem.value?.fgImage || ''
})

// 设置活动标签页
const setActiveTab = (tab: string) => {
  activeTab.value = tab
}

// 监听标签切换，触发动画
watch(activeTab, () => {
  playAutoAnimation()
})

// 播放自动动画
const playAutoAnimation = () => {
  // 清除之前的动画
  if (animationTimer) {
    clearTimeout(animationTimer)
  }

  isAnimating.value = true

  // 重置位置
  clipPosition.value = 0

  // 动画序列
  setTimeout(() => {
    clipPosition.value = 100
  }, 100)

  setTimeout(() => {
    clipPosition.value = 0
  }, 800)

  setTimeout(() => {
    clipPosition.value = 50
    isAnimating.value = false
  }, 1500)
}

// 开始拖动
const startDrag = (e: MouseEvent | TouchEvent) => {
  e.preventDefault()
  isDragging.value = true

  const moveHandler = (e: MouseEvent | TouchEvent) => {
    if (!isDragging.value) return

    const viewer = document.querySelector('.comparison-viewer') as HTMLElement
    if (!viewer) return

    const rect = viewer.getBoundingClientRect()
    const clientY = e instanceof MouseEvent ? e.clientY : e.touches[0].clientY
    const relativeY = clientY - rect.top
    const percentage = (relativeY / rect.height) * 100

    // 限制在 0-100 之间
    clipPosition.value = Math.max(0, Math.min(100, percentage))
  }

  const endHandler = () => {
    isDragging.value = false
    document.removeEventListener('mousemove', moveHandler)
    document.removeEventListener('touchmove', moveHandler)
    document.removeEventListener('mouseup', endHandler)
    document.removeEventListener('touchend', endHandler)
  }

  document.addEventListener('mousemove', moveHandler)
  document.addEventListener('touchmove', moveHandler)
  document.addEventListener('mouseup', endHandler)
  document.addEventListener('touchend', endHandler)
}

// 组件挂载时播放初始动画
onMounted(() => {
  setTimeout(() => {
    playAutoAnimation()
  }, 500)
})

// 清理定时器
onUnmounted(() => {
  if (animationTimer) {
    clearTimeout(animationTimer)
  }
})
</script>

<style lang="less" scoped>
.comparison-viewer {
  position: relative;
  overflow: hidden;
  border-radius: 40px;
  aspect-ratio: 11 / 19; // iPhone比例

  .image-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .fg-layer {
    z-index: 2;
  }

  .drag-handle {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 10;
    cursor: ns-resize;

    .handle-icon {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 25px;
      height: 25px;
      background-color: #6155ff;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 18px rgba(0, 0, 0, 0.3);
      z-index: 2;

      svg {
        color: #ffffff;
      }
    }

    .handle-line {
      position: absolute;
      left: 0;
      right: 0;
      top: 50%;
      height: 3px;
      background: rgba(255, 255, 255, 0.8);
      transform: translateY(-50%);
      pointer-events: none;

      &::before,
      &::after {
        content: '';
        position: absolute;
        top: 0;
        width: 50%;
        height: 100%;
        background: inherit;
      }

      &::before {
        left: -50%;
      }

      &::after {
        right: -50%;
      }
    }
  }
}
</style>
